import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Book, Cycle, PrintRun, Items, Product, ProductComponent } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Plus, Search, BookCopy, Edit, Eye, Package } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import BookForm from './BookForm';
import CycleForm from './CycleForm';
import ProductsModal from './ProductsModal';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const BooksView = React.memo(() => {
  const { t } = useTranslation();
    const [books, setBooks] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [printRuns, setPrintRuns] = useState([]);
    const [bookEditions, setBookEditions] = useState([]);
    const [products, setProducts] = useState([]);
    const [productComponents, setProductComponents] = useState([]);
    const [filteredBooks, setFilteredBooks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isBookModalOpen, setIsBookModalOpen] = useState(false);
    const [isCycleModalOpen, setIsCycleModalOpen] = useState(false);
    const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
    const [selectedBook, setSelectedBook] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Debounce search term
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        fetchData();
    }, [dataLoaded]); // Загружаем при монтировании и когда dataLoaded изменяется

    useEffect(() => {
        filterBooks();
    }, [books, debouncedSearchTerm]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [booksData, cyclesData, printRunsData, editionsData, productsData, componentsData] = await Promise.all([
                Book.list(),
                Cycle.list(),
                PrintRun.list(),
                Items.list({ type: 'book_edition' }),
                Product.list(),
                ProductComponent.list()
            ]);
            setBooks(booksData);
            setCycles(cyclesData);
            setPrintRuns(printRunsData);
            console.log('Loaded book editions:', editionsData);
            console.log('Sample edition metadata:', editionsData[0]?.metadata);
            setBookEditions(editionsData);
            setProducts(productsData);
            setProductComponents(componentsData);
            setDataLoaded(true);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        setIsLoading(false);
    }, []); // Убираю зависимость от dataLoaded

    const filterBooks = useCallback(() => {
        let filtered = books;
        if (debouncedSearchTerm) {
            filtered = filtered.filter(book =>
                book.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                book.author?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            );
        }
        setFilteredBooks(filtered);
    }, [books, debouncedSearchTerm]);

    const handleCreateBook = () => {
        setSelectedBook(null);
        setIsBookModalOpen(true);
    };

    const handleEditBook = (book) => {
        console.log('Editing book:', book);
        console.log('Available bookEditions:', bookEditions);
        setSelectedBook(book);
        setIsBookModalOpen(true);
    };

    const handleViewProducts = (book) => {
        setSelectedBook(book);
        setIsProductsModalOpen(true);
    };

    const handleCreateCycle = () => {
        setIsCycleModalOpen(true);
    };

    const handleBookSave = async () => {
        setIsBookModalOpen(false);
        // Принудительно обновляем данные
        await fetchData();
    };

    const handleCycleSave = async () => {
        setIsCycleModalOpen(false);
        // Принудительно обновляем данные
        await fetchData();
    };

    const getCycleName = (cycleId) => {
        const cycle = cycles.find(c => c.id === cycleId);
        return cycle ? cycle.name : null;
    };

    const getBookStats = (bookId) => {
        const bookPrintRuns = printRuns.filter(pr => pr.book_id === bookId);
        const totalPrinted = bookPrintRuns.reduce((sum, pr) => sum + pr.quantity, 0);
        const deliveredPrintRuns = bookPrintRuns.filter(pr => pr.status === 'delivered');
        const totalDelivered = deliveredPrintRuns.reduce((sum, pr) => sum + pr.quantity, 0);
        
        return { totalPrinted, totalDelivered, activeRuns: bookPrintRuns.length };
    };

    const getBookProducts = (bookId) => {
        const bookComponents = productComponents.filter(pc => pc.book_id === bookId);
        return bookComponents.map(component => {
            const product = products.find(p => p.id === component.product_id);
            return product ? { ...product, quantity: component.quantity } : null;
        }).filter(Boolean);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-slate-800">{t('inventory.books.title')}</h2>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCreateCycle}>
                        <BookCopy className="w-4 h-4 mr-2" />
                        {t('inventory.books.new_series')}
                    </Button>
                    <Button onClick={handleCreateBook} className="bg-amber-600 hover:bg-amber-700">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('inventory.books.add_book')}
                    </Button>
                </div>
            </div>

            <Card className="elegant-card">
                <CardContent className="p-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <Input
                            placeholder={t('inventory.books.search_placeholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                        />
                    </div>
                </CardContent>
            </Card>

            <Card className="elegant-card">
              <CardContent className="p-0">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="font-bold text-slate-900">{t('inventory.books.table.title')}</TableHead>
                      <TableHead className="font-bold text-slate-900">{t('inventory.books.table.author')}</TableHead>
                      <TableHead className="font-bold text-slate-900">{t('inventory.books.table.series')}</TableHead>
                      <TableHead className="font-bold text-slate-900">{t('inventory.books.table.print_runs')}</TableHead>
                      <TableHead className="font-bold text-slate-900">{t('inventory.books.table.sets')}</TableHead>
                      <TableHead className="font-bold text-slate-900">{t('inventory.books.table.printed')}</TableHead>
                      <TableHead className="font-bold text-slate-900">{t('inventory.books.table.delivered')}</TableHead>
                      <TableHead className="font-bold text-slate-900 text-right">{t('inventory.books.table.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBooks.map(book => {
                        const stats = getBookStats(book.id);
                        const cycleName = getCycleName(book.cycle_id);
                        const bookProducts = getBookProducts(book.id);
                        
                        return (
                            <TableRow key={book.id} className="hover:bg-slate-50/50">
                                <TableCell>
                                    <div>
                                        <p className="font-semibold text-slate-900">{book.title}</p>
                                        {book.description && (
                                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{book.description}</p>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="text-slate-700">{book.author}</TableCell>
                                <TableCell>
                                    {cycleName ? (
                                        <Badge variant="outline" className="border-blue-200 text-blue-800">
                                            {cycleName}
                                        </Badge>
                                    ) : '-'}
                                </TableCell>
                                <TableCell>
                                    <Badge className="bg-slate-100 text-slate-800">
                                        {stats.activeRuns} {t('inventory.books.units.print_runs')}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {bookProducts.length > 0 ? (
                                        <div className="flex items-center gap-2">
                                            <Badge className="bg-purple-100 text-purple-800">
                                                {bookProducts.length} {t('inventory.books.units.sets')}
                                            </Badge>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleViewProducts(book)}
                                            >
                                                <Eye className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <span className="text-slate-400">-</span>
                                    )}
                                </TableCell>
                                <TableCell className="font-semibold text-slate-900">
                                    {stats.totalPrinted.toLocaleString()} {t('inventory.books.units.copies')}
                                </TableCell>
                                <TableCell className="font-semibold text-green-600">
                                    {stats.totalDelivered.toLocaleString()} {t('inventory.books.units.copies')}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => handleEditBook(book)}>
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <BookForm
                isOpen={isBookModalOpen}
                onClose={() => setIsBookModalOpen(false)}
                book={selectedBook}
                cycles={cycles}
                onSave={handleBookSave}
                bookEditions={selectedBook ? bookEditions.filter(be => {
                    console.log('Filtering edition:', be, 'for book:', selectedBook.id);
                    // Проверяем book_id в метаданных
                    if (be.metadata?.book_id === selectedBook.id) {
                        return true;
                    }
                    // Если book_id нет, проверяем название (например, "bbb - 1" для книги "bbb")
                    if (be.name && be.name.startsWith(selectedBook.title + ' - ')) {
                        return true;
                    }
                    return false;
                }) : []}
            />

            <CycleForm
                isOpen={isCycleModalOpen}
                onClose={() => setIsCycleModalOpen(false)}
                onSave={handleCycleSave}
            />

            <ProductsModal
                isOpen={isProductsModalOpen}
                onClose={() => setIsProductsModalOpen(false)}
                book={selectedBook}
                products={products}
                productComponents={productComponents}
                onRefresh={fetchData}
            />
        </div>
    );
});

export default BooksView;